import {
  Component,
  DestroyRef,
  inject,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CoreService } from '@app/@core/services/core.service';
import { ProfileService } from '@app/profile/profile.service';
import { ProfileDetailsComponent } from '../profile-details.component';
import { TranslateService } from '@ngx-translate/core';
import { interval, Subscription } from 'rxjs';

export type OtpDialogMode = 'verify-phone' | 'delete-account';

export interface OtpDialogData {
  mode: OtpDialogMode;
  phoneNumber?: string;
}

@Component({
  selector: 'app-otp-dialog',
  templateUrl: './otp-dialog.component.html',
  styleUrls: ['./otp-dialog.component.scss'],
  standalone: false,
})
export class OtpDialogComponent implements OnInit, OnDestroy {
  otpControl = new FormControl('');
  otpConfig = { length: 6, allowNumbersOnly: true };
  error: string;
  isSubmitting = false;
  isResending = false;
  isTimerActive = false;
  timer: number;
  private timerSubscription: Subscription;

  get mode(): OtpDialogMode {
    return typeof this.data === 'object' ? this.data.mode : 'verify-phone';
  }

  get phone(): string {
    return typeof this.data === 'string' ? this.data : this.data?.phoneNumber;
  }

  get phoneSuffix(): string {
    return this.phone ? this.phone.slice(-3) : '';
  }

  constructor(
    private profileService: ProfileService,
    private coreService: CoreService,
    private translateService: TranslateService,
    private dialogRef: MatDialogRef<ProfileDetailsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OtpDialogData | string,
  ) {}

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.startTimer(60);
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  closeOtpDialog() {
    this.dialogRef.close(false);
  }

  submitOtp() {
    if (this.mode === 'delete-account') {
      this.submitDeleteOtp();
    } else {
      this.submitVerifyPhoneOtp();
    }
  }

  resendOtp() {
    this.isResending = true;
    this.otpControl.reset('');
    this.error = null;
    this.profileService
      .sendOtp({ phoneNumber: this.phone })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isResending = false;
          this.startTimer(60);
        },
        error: (err) => {
          this.isResending = false;
          this.coreService.showErrorDialog(err);
        },
      });
  }

  getTimerText(sec: number): string {
    if (sec > 60) {
      const m = Math.floor(sec / 60);
      const s = sec % 60;
      return `${m} ${this.translateService.instant('minute(s)')} ${this.translateService.instant('and')} ${s} ${this.translateService.instant('second(s)')}`;
    }
    return `${sec} ${this.translateService.instant('second(s)')}`;
  }

  private startTimer(duration: number) {
    this.stopTimer();
    this.isTimerActive = true;
    this.timer = duration;
    this.timerSubscription = interval(1000).subscribe(() => {
      this.timer--;
      if (this.timer <= 0) this.stopTimer();
    });
  }

  private stopTimer() {
    this.timerSubscription?.unsubscribe();
    this.isTimerActive = false;
  }

  private submitVerifyPhoneOtp() {
    this.isSubmitting = true;
    this.profileService
      .verifyOtp(this.otpControl.value, this.phone)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.isSubmitting = false;
          this.coreService.showErrorDialog(err);
          this.error = err?.error?.error ?? err?.error?.errors?.[0];
        },
      });
  }

  private submitDeleteOtp() {
    this.error = null;
    this.isSubmitting = true;
    this.profileService
      .confirmPermanentDelete(this.otpControl.value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.isSubmitting = false;
          const code: string = err?.error?.errors?.[0];
          if (code === 'InvalidOTP') {
            this.error = this.translateService.instant(
              'account.delete.invalidOtp',
            );
          } else if (code === 'DeleteCustomerCoolOffNotFinished') {
            this.coreService.notify(
              this.translateService.instant(
                'account.delete.coolOffNotFinished',
              ),
            );
            this.dialogRef.close(false);
          } else if (code === 'NoPendingDeletionRequest') {
            this.coreService.showErrorDialog(
              this.translateService.instant('account.delete.noPendingRequest'),
            );
            this.dialogRef.close(false);
          } else if (code?.startsWith('DeleteCustomerNotEligible')) {
            this.coreService.showErrorDialog(
              this.translateService.instant(`blocking.${code}`),
            );
            this.dialogRef.close(false);
          } else {
            this.coreService.showErrorDialog(err);
          }
        },
      });
  }
}
